import client from 'services/apollo-client';
import Response from 'services/graphql/response';
import ResponseError from 'services/graphql/response-error';
import { omit, isPlainObject, isArray, isEmpty } from 'lodash';
import { compose, entries, last, size } from 'lodash/fp';
import { notEmpty, asyncAction } from 'utils/js';

const selector = (response) => {
  const { data } = response;

  if (size(data) > 1) {
    return data;
  }

  return compose(
    ([, data]) => data,
    // @ts-ignore
    last,
    entries,
  )(data);
};

export const executeRequest = async (callable) => {
  const [error, response] = await asyncAction(callable());

  if (error) {
    return new ResponseError(error);
  }

  const data = selector(response);

  if (!data?.data && data?.error) {
    return new ResponseError(new Error(data.error));
  }

  return new Response(parseData(data), data.meta);
};

export const mutation = (
  mutation,
  options = { fetchPolicy: 'no-cache' },
) => async (variables?, ...rest) => {
  const [mutationOptions = options] = rest;

  const callable = () =>
    client.mutate({
      mutation,
      ...(variables && { variables: removeTypeName(variables) }),
      ...mutationOptions,
    });

  return executeRequest(callable);
};

export const query = (
  query,
  options = { errorPolicy: 'all', fetchPolicy: 'network-only' },
) => async (variables?, ...rest) => {
  const [queryOptions = options] = rest;
  const callable = () => client.query({ query, variables, ...queryOptions });

  return executeRequest(callable);
};

export function removeTypeName(variables = {}) {
  return Object.entries(variables).reduce((variables, [key, value]) => {
    let keyValue = value;

    if (isPlainObject(value)) {
      keyValue = removeTypeName(omit(value as any, ['__typename']));
    }

    if (isArray(value) && notEmpty(value)) {
      keyValue = (value as Array<any>).map((item) => {
        if (isPlainObject(item)) {
          return removeTypeName(omit(item, ['__typename']));
        }

        return item;
      });
    }

    return { ...variables, [key]: keyValue };
  }, {});
}

function parseData(response) {
  if (response.edges) {
    return response.edges.map(({ node }) => node);
  }

  if (response.nodes) {
    return response.nodes;
  }

  return response && isEmpty(response) ? null : response;
}
